import React, { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Phone, MapPin, Mail, MessageCircle, Clock } from "lucide-react";
import useDocumentTitle from "../lib/useDocumentTitle";

export default function Contact() {
  useDocumentTitle("Contact Us", "Get in touch with HouseDesignWala™ — call, WhatsApp or email us for a free 15-minute architect consultation before you order.");
  const [form, setForm] = useState({ name: "", phone: "", city: "", service_interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll reply within 15 minutes.");
      setForm({ name: "", phone: "", city: "", service_interest: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="bg-[#FEF3E2] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F7941D]">Contact</span>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-[#1a3c5e] mt-2">Let's Talk About Your Dream Home</h1>
          <p className="mt-3 text-gray-700">We typically reply within <span className="font-semibold text-[#F7941D]">15 minutes</span>.</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-5 gap-10">
        <form onSubmit={submit} className="lg:col-span-3 bg-white rounded-2xl shadow-md border border-[#1a3c5e]/10 p-6 sm:p-8 space-y-4" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-[#1a3c5e]">Send us a message</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#1a3c5e] block mb-1">Name *</label>
              <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required data-testid="contact-name" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1a3c5e] block mb-1">Phone *</label>
              <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} required data-testid="contact-phone" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-semibold text-[#1a3c5e] block mb-1">City</label>
              <input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} data-testid="contact-city" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1a3c5e] block mb-1">Interested in</label>
              <select value={form.service_interest} onChange={(e) => setForm({...form, service_interest: e.target.value})} data-testid="contact-interest" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D] bg-white">
                <option value="">Select package</option>
                <option value="basic">Basic — ₹8,500</option>
                <option value="premium">Premium — ₹13,500</option>
                <option value="luxury">Luxury — ₹20,500</option>
                <option value="custom">Custom requirement</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-semibold text-[#1a3c5e] block mb-1">Message</label>
            <textarea rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="contact-message" className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
          </div>
          <button disabled={submitting} type="submit" data-testid="contact-submit" className="w-full py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-semibold rounded-xl shadow-md transition">
            {submitting ? "Sending..." : "Send Message"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          <a href="https://wa.me/919050785528" target="_blank" rel="noopener noreferrer" className="block bg-[#25D366] text-white rounded-2xl p-6 hover:bg-[#128C7E] transition shadow-lg" data-testid="contact-whatsapp">
            <MessageCircle className="w-8 h-8 mb-3" />
            <div className="font-display text-xl font-bold">Chat on WhatsApp</div>
            <div className="text-sm text-white/90 mt-1">Fastest way — usually replied in 15 min.</div>
          </a>
          <div className="bg-white rounded-2xl p-6 border border-[#1a3c5e]/10 space-y-4">
            <div className="flex gap-3">
              <Phone className="w-5 h-5 text-[#F7941D] mt-0.5" />
              <div>
                <div className="text-xs text-gray-500 font-semibold uppercase">Phone</div>
                <a href="tel:+919050782248" className="text-[#1a3c5e] font-semibold">+91 90507 82248</a>
              </div>
            </div>
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-[#F7941D] mt-0.5" />
              <div>
                <div className="text-xs text-gray-500 font-semibold uppercase">Email</div>
                <a href="mailto:support@housedesignwala.com" className="text-[#1a3c5e] font-semibold">support@housedesignwala.com</a>
              </div>
            </div>
            <div className="flex gap-3">
              <MapPin className="w-5 h-5 text-[#F7941D] mt-0.5" />
              <div>
                <div className="text-xs text-gray-500 font-semibold uppercase">Service Area</div>
                <div className="text-[#1a3c5e] font-semibold">Pan India · 100% Online</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-[#F7941D] mt-0.5" />
              <div>
                <div className="text-xs text-gray-500 font-semibold uppercase">Hours</div>
                <div className="text-[#1a3c5e] font-semibold">Mon–Sat 9am–8pm IST</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
